import React, { useCallback, useEffect, useState } from 'react';
import {
  Button, ButtonGroup, ClickAwayListener,
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { ArrowLeft, ArrowRight } from '@mui/icons-material';
import {
  decreaseStartDuration,
  formatTimeRange,
  increaseStartDuration,
  moveTowardsFuture,
  moveTowardsPast,
  RecentRelativeTimeRange, setStartDuration,
  TimeRange,
} from 'utils/time';
import Popover from 'screens/shared/popover';
import { _t, _tf } from 'utils';
import DateTimePicker from './date-time-picker';

export default ({ onChange, value }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [visible, setVisible] = useState(false);

  const [startTime, setStartTime] = useState(new Date());
  const [endTime, setEndTime] = useState(new Date());

  const [shouldHandleClickAway, setShouldHandleClickAway] = useState(true);

  const [timeDisplay, setTimeDisplay] = useState('');

  const getTimeDisplay = useCallback(formatTimeRange, []);

  useEffect(() => {
    setStartTime(value.startDate);
    setEndTime(value.endDate);
    setTimeDisplay(getTimeDisplay(value));
  }, [value, getTimeDisplay]);

  const handleClickAway = () => {
    if (shouldHandleClickAway) {
      setVisible(false);
      setAnchorEl(null);
    }
  };

  const applyAbsoluteRange = () => {
    onChange(TimeRange(new Date(startTime), new Date(endTime)));
    handleClickAway();
  };

  const handleLinkClick = (event, relativeRange) => {
    event.preventDefault();
    onChange(RecentRelativeTimeRange({ unit: relativeRange.unit, range: relativeRange.amount }));
    handleClickAway();
  };

  const guardSetEndTime = (candidateEndTime, currentStartTime) => {
    if (!currentStartTime) {
      currentStartTime = startTime;
    }

    if (candidateEndTime < currentStartTime) {
      candidateEndTime = new Date(currentStartTime + 15 * 60 * 1000);
    }
    setEndTime(candidateEndTime);
  };

  const guardSetStartTime = (newStartTime) => {
    setStartTime(newStartTime);
    guardSetEndTime(endTime, newStartTime);
  };

  const handleOpen = () => {
    setShouldHandleClickAway(false);
  };

  const handleClose = () => {
    setTimeout(() => setShouldHandleClickAway(true), 50);
  };

  const handleToggle = (e) => {
    if (visible) {
      setAnchorEl(null);
      setVisible(false);
    } else {
      setAnchorEl(e.target);
      setVisible(true);
    }
  };

  const handleLeftArrow = () => {
    if (value.relative) {
      onChange(increaseStartDuration(value, value.duration));
    } else {
      onChange(moveTowardsPast(value, value.duration));
    }
  };

  const handleRightArrow = () => {
    if (!value.relative) {
      onChange(moveTowardsFuture(value, value.duration));
      return;
    }
    const newDuration = Math.floor(value.duration / 2);
    if (newDuration < 60 * 1000) {
      onChange(setStartDuration(value, 60 * 1000));
    } else {
      onChange(decreaseStartDuration(value, newDuration));
    }
  };

  const units = [
    { unit: 'minutes', amount: 15 },
    { unit: 'minutes', amount: 30 },
    { unit: 'hours', amount: 1 },
    { unit: 'hours', amount: 3 },
    { unit: 'hours', amount: 6 },
    { unit: 'hours', amount: 12 },
    { unit: 'days', amount: 1 },
    { unit: 'days', amount: 2 },
    { unit: 'weeks', amount: 1 },
    { unit: 'weeks', amount: 2 },
  ];

  const links = units.map((relativeRange, i) => {
    const roundedAmount = Math.round(relativeRange.amount);
    const unitText = roundedAmount === 1 ? relativeRange.unit.slice(0, relativeRange.unit.length - 1) : relativeRange.unit;
    const text = _tf('Last {amount} {unit}', { amount: roundedAmount, unit: _t(unitText) });
    return (
      <Button
        key={i}
        variant="outlined"
        size="small"
        color="primary"
        onClick={(e) => handleLinkClick(e, relativeRange)}
        style={{ marginBottom: 10 }}
      >
        {text}
      </Button>
    );
  });

  const renderedItem = (
    <ClickAwayListener onClickAway={handleClickAway}>
      <div style={{ display: 'flex' }}>
        <div style={{ width: '50%' }}>
          <h2>{_t('Absolute')}</h2>
          <DateTimePicker label={_t('From')} value={startTime} onChange={guardSetStartTime} onOpen={handleOpen} onClose={handleClose} />
          <DateTimePicker
            label={_t('To')}
            minDateTime={startTime}
            value={endTime}
            onChange={guardSetEndTime}
            onOpen={handleOpen}
            onClose={handleClose}
          />
          <Button
            variant="outlined"
            size="small"
            color="primary"
            onClick={applyAbsoluteRange}
            style={{ marginBottom: 10 }}
          >
            {_t('Apply Time Range')}
          </Button>
        </div>
        <div style={{
          paddingLeft: 10, width: '50%', display: 'flex', flexDirection: 'column',
        }}
        >
          <h2>{_t('Relative')}</h2>
          {links}
        </div>
      </div>
    </ClickAwayListener>
  );

  return (
    <>
      <ButtonGroup fullWidth variant="outlined" color="primary" aria-label="split button">
        <Button onClick={handleLeftArrow} style={{ width: 70 }}><ArrowLeft /></Button>
        <Button onClick={handleToggle}>{timeDisplay}</Button>
        <Button
          color="primary"
          size="small"
          aria-controls={visible ? 'split-button-menu' : undefined}
          aria-expanded={visible ? 'true' : undefined}
          aria-label="select merge strategy"
          aria-haspopup="menu"
          style={{ width: 50 }}
          onClick={handleToggle}
        >
          <ArrowDropDownIcon />
        </Button>
        <Button onClick={handleRightArrow} style={{ width: 70 }}><ArrowRight /></Button>
      </ButtonGroup>
      <Popover
        style={{ minWidth: 400 }}
        outerStyle={{ zIndex: 110 }}
        anchorEl={anchorEl}
        open={visible}
        render={renderedItem}
      />
    </>
  );
};
